import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { DesignPartnerDomainsService } from '../../../services/design-partner-domains.service';
import { ToastService } from '../../../services/toast.service';
import { CertificateDetailModalComponent } from '../certificate-detail-modal/certificate-detail-modal.component';

@Component({
  selector: 'app-monitored-domains',
  standalone: true,
  imports: [CommonModule, FormsModule, CertificateDetailModalComponent],
  templateUrl: './monitored-domains.component.html',
  styleUrl: './monitored-domains.component.less',
})
export class MonitoredDomainsComponent implements OnInit {
  records: any[] = [];
  filteredRecords: any[] = [];
  isLoading: boolean = false;
  noRecordsMessage: string = 'No monitored subdomains found.';
  searchQuery: string = '';

  currentPage: number = 1;
  readonly pageSize: number = 10;

  selectedRecord: any = null;
  openMenuIndex: number | null = null;
  refreshInProgress: { [domain: string]: boolean } = {};
  stopInProgress: { [domain: string]: boolean } = {};
  pendingStopRecord: any = null;

  constructor(
    private domainsService: DesignPartnerDomainsService,
    private toaster: ToastService
  ) {}

  ngOnInit(): void {
    this.loadRecords();
  }

  loadRecords(): void {
    this.isLoading = true;
    this.domainsService.getMonitoredDomains().subscribe({
      next: (response: any) => {
        const raw = response?.records ?? response?.data ?? response ?? [];
        const list = Array.isArray(raw) ? raw : [];
        this.records = list.map((item: any) => this.normalizeRecord(item));
        this.filteredRecords = [...this.records];
        this.isLoading = false;
        if (this.records.length === 0) {
          this.noRecordsMessage = 'No monitored subdomains found. Start monitoring a subdomain from Domains > View Subdomains.';
        }
      },
      error: () => {
        this.isLoading = false;
        this.noRecordsMessage = 'Failed to load monitored subdomains. Please try again later.';
        this.toaster.showToast('Failed to load monitored subdomains', 'error');
      },
    });
  }

  // API responses come back snake_case (common_name, not_after, ...); the modal/table
  // are built against the camelCase shape certkit returns, so normalize once on load.
  private normalizeRecord(item: any): any {
    return {
      ...item,
      domain: item.domain,
      commonName: item.commonName ?? item.common_name,
      serialNumber: item.serialNumber ?? item.serial_number,
      notBefore: item.notBefore ?? item.not_before,
      notAfter: item.notAfter ?? item.not_after,
      dnsNames: item.dnsNames ?? item.dns_names,
      issuerOrganization: item.issuerOrganization ?? item.issuer_organization,
      issuerCommonName: item.issuerCommonName ?? item.issuer_common_name,
      isWildcard: item.isWildcard ?? item.is_wildcard,
      isPrecert: item.isPrecert ?? item.is_precert,
      insertedTime: item.insertedTime ?? item.last_inserted_time,
      riskLevel: item.riskLevel ?? item.risk_level,
      tls: item.tls ?? [],
    };
  }

  get totalPages(): number {
    return Math.ceil(this.filteredRecords.length / this.pageSize);
  }

  get paginatedRecords(): any[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredRecords.slice(start, start + this.pageSize);
  }

  onSearchChange(): void {
    const lowerCaseQuery = this.searchQuery.toString().toLowerCase();
    this.filteredRecords = this.records.filter((item) => {
      const domain = (item.domain || item.commonName || '').toString().toLowerCase();
      const issuer = (item.issuerOrganization || '').toString().toLowerCase();
      return domain.includes(lowerCaseQuery) || issuer.includes(lowerCaseQuery);
    });
    this.currentPage = 1;
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.openMenuIndex = null;
    }
  }

  getDaysRemaining(notAfter: string): number {
    return Math.ceil((new Date(notAfter).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  }

  getDaysClass(notAfter: string): string {
    const days = this.getDaysRemaining(notAfter);
    if (days < 0) return 'days-expired';
    if (days <= 30) return 'days-critical';
    if (days <= 90) return 'days-warning';
    return 'days-ok';
  }

  getRiskClass(riskLevel: string): string {
    switch (riskLevel?.toUpperCase()) {
      case 'CRITICAL': return 'risk-critical';
      case 'HIGH':     return 'risk-high';
      case 'MEDIUM':   return 'risk-medium';
      default:         return 'risk-low';
    }
  }

  openPopup(item: any): void {
    this.selectedRecord = item;
  }

  closePopup(): void {
    this.selectedRecord = null;
  }

  toggleMenu(index: number, event: MouseEvent): void {
    event.stopPropagation();
    this.openMenuIndex = this.openMenuIndex === index ? null : index;
  }

  refreshMonitoring(item: any): void {
    this.openMenuIndex = null;
    const subDomain = item.domain || item.commonName;
    if (this.refreshInProgress[subDomain]) {
      return;
    }

    this.refreshInProgress[subDomain] = true;
    this.domainsService.buildLatestMonitoringRecord(subDomain).pipe(
      catchError(() => of(undefined))
    ).subscribe((record: any) => {
      if (record === undefined) {
        this.refreshInProgress[subDomain] = false;
        this.toaster.showToast('Failed to fetch certificate data for monitoring', 'error');
        return;
      }

      if (!record) {
        this.refreshInProgress[subDomain] = false;
        this.toaster.showToast('No certificate records found for this domain', 'error');
        return;
      }

      const payload = { ...record, domain: subDomain };
      this.domainsService.refreshMonitoring(payload).subscribe({
        next: () => {
          this.refreshInProgress[subDomain] = false;
          const idx = this.records.findIndex((r) => (r.domain || r.commonName) === subDomain);
          if (idx >= 0) {
            this.records[idx] = { ...this.records[idx], ...payload };
            const filteredIdx = this.filteredRecords.findIndex((r) => (r.domain || r.commonName) === subDomain);
            if (filteredIdx >= 0) {
              this.filteredRecords[filteredIdx] = this.records[idx];
            }
          }
          this.toaster.showToast(`Monitoring refreshed for ${subDomain}`, 'success');
        },
        error: (err) => {
          this.refreshInProgress[subDomain] = false;
          const message = err?.error?.message || 'Failed to refresh monitoring';
          this.toaster.showToast(message, 'error');
        },
      });
    });
  }

  requestStopMonitoring(item: any): void {
    this.openMenuIndex = null;
    this.pendingStopRecord = item;
  }

  cancelStopMonitoring(): void {
    if (this.stopInProgress[this.pendingStopDomain]) {
      return;
    }
    this.pendingStopRecord = null;
  }

  get pendingStopDomain(): string {
    return this.pendingStopRecord?.domain || this.pendingStopRecord?.commonName || '';
  }

  confirmStopMonitoring(): void {
    const item = this.pendingStopRecord;
    if (!item) {
      return;
    }

    const subDomain = item.domain || item.commonName;
    if (this.stopInProgress[subDomain]) {
      return;
    }

    this.stopInProgress[subDomain] = true;
    this.domainsService.stopMonitoring(subDomain).subscribe({
      next: () => {
        delete this.stopInProgress[subDomain];
        this.pendingStopRecord = null;
        this.records = this.records.filter((r) => (r.domain || r.commonName) !== subDomain);
        this.filteredRecords = this.filteredRecords.filter((r) => (r.domain || r.commonName) !== subDomain);
        if (this.currentPage > this.totalPages) {
          this.currentPage = Math.max(1, this.totalPages);
        }
        if (this.records.length === 0) {
          this.noRecordsMessage = 'No monitored subdomains found. Start monitoring a subdomain from Domains > View Subdomains.';
        }
        this.toaster.showToast(`Stopped monitoring ${subDomain}`, 'success');
      },
      error: (err) => {
        delete this.stopInProgress[subDomain];
        this.pendingStopRecord = null;
        const message = err?.error?.error || err?.error?.message || 'Failed to stop monitoring';
        this.toaster.showToast(message, 'error');
      },
    });
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.openMenuIndex = null;
  }
}
